import { useEffect, useRef } from "react";
import { koloSliceStep, type KoloPalette } from "@ziolo/shared";

const SITE_COLORS = [
  "#4caf50",
  "#66bb6a",
  "#26a69a",
  "#8d6e63",
  "#81c784",
  "#42a5f5",
  "#6d4c41",
  "#78909c",
  "#2e7d32",
  "#29b6f6",
  "#388e3c",
  "#546e7a",
];

const VIVID_COLORS = [
  "#FF793F",
  "#FFB142",
  "#33D9B2",
  "#34ACE0",
  "#706FD3",
  "#CD84F1",
  "#3ae374",
  "#F97F51",
  "#B33771",
  "#ffaf40",
  "#18dcff",
  "#7d5fff",
];

interface WheelProps {
  options: string[];
  rotation: number;
  palette: KoloPalette;
}

export function KoloWheel({ options, rotation, palette }: WheelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const frame = frameRef.current;
    if (!canvas || !frame) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const draw = () => {
      const size = frame.clientWidth;
      if (size < 32) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(size * dpr);
      canvas.height = Math.floor(size * dpr);
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const cx = size / 2;
      const cy = size / 2;
      const radius = size / 2 - 8;

      ctx.clearRect(0, 0, size, size);

      if (options.length === 0) {
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(45, 45, 45, 0.9)";
        ctx.fill();
        ctx.strokeStyle = "rgba(76, 175, 80, 0.45)";
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.fillStyle = "#aaaaaa";
        ctx.font = "16px 'Roboto Mono', monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Dodaj opcje", cx, cy - 10);
        ctx.font = "13px 'Roboto Mono', monospace";
        ctx.fillStyle = "#888888";
        ctx.fillText("minimum dwie", cx, cy + 14);
        return;
      }

      const step = koloSliceStep(options.length);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#121212";

      for (let i = 0; i < options.length; i += 1) {
        const start = rotation + i * step;
        const end = start + step;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, radius, start, end);
        ctx.closePath();
        ctx.fillStyle = (palette === "vivid" ? VIVID_COLORS : SITE_COLORS)[i % 12] ?? "#4caf50";
        ctx.fill();
        ctx.stroke();

        const mid = start + step / 2;
        const labelRadius = radius * 0.62;
        const x = cx + Math.cos(mid) * labelRadius;
        const y = cy + Math.sin(mid) * labelRadius;
        let label = options[i] ?? "";
        if (label.length > 14) label = `${label.slice(0, 13)}…`;

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(mid + Math.PI / 2);
        ctx.fillStyle = "#ffffff";
        ctx.font = `700 ${Math.max(11, radius / 14)}px 'Roboto Mono', monospace`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
        ctx.shadowBlur = 4;
        ctx.fillText(label, 0, 0);
        ctx.restore();
      }

      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(18, radius * 0.08), 0, Math.PI * 2);
      ctx.fillStyle = "#121212";
      ctx.fill();
      ctx.strokeStyle = "#4caf50";
      ctx.lineWidth = 3;
      ctx.stroke();
    };

    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [options, rotation, palette]);

  return (
    <div className="kolo-wheel" ref={frameRef}>
      <div className="kolo-wheel__pointer" aria-hidden="true" />
      <canvas ref={canvasRef} className="kolo-wheel__canvas" />
    </div>
  );
}
